import ATSSimulator from './core/ATSSimulator';
import HumanScanner from './core/HumanScanner';
import RealityScorer from './core/RealityScorer';
import { qualityCheck } from './core/utils/quality';

// Configurable weights (keep in sync with README)
const WEIGHTS = {
  spellingGrammar: 20,
  exactKeyword: 20,
  semantic: 10,
  quantified: 15,
  transferable: 15,
  techCurrency: 10,
  layout: 10,
};

/**
 * New combined checker following the 7–factor rubric.
 * It keeps the same public API: await scorer.check({resumeText, jobDescription})
 */
export default class ResumeRealityCheckerV2 {
  constructor(options = {}) {
    this.ats = new ATSSimulator();
    this.human = new HumanScanner();
    this.reality = new RealityScorer();
    this.options = options;
  }

  async check({ resumeText, jobDescription }) {
    // Factor 1 – spelling & grammar
    const quality = qualityCheck(resumeText || '');
    const score1 = Math.max(
      0,
      WEIGHTS.spellingGrammar - (quality.typoPenalty + quality.grammarPenalty)
    );

    // Re-use existing ATS simulator for keyword + layout
    const atsResult = await this.ats.simulateATSFiltering(resumeText, jobDescription);
    const keywordRaw = atsResult.breakdown.keywordMatching || 0;
    // Map 0-95 range → 0-20
    const score2 = Math.round((keywordRaw / 95) * WEIGHTS.exactKeyword);

    // Layout pass/fail (use formatCompatibility from old code)
    const layoutRaw = atsResult.breakdown.formatCompatibility || 100;
    const score7 = layoutRaw === 100 ? WEIGHTS.layout : 0;

    // Semantic – not yet implemented, placeholder 0 or via callback
    let score3 = 0;
    if (this.options.semanticMatchFn) {
      try {
        const semanticRatio = await this.options.semanticMatchFn(resumeText, jobDescription); // 0-1
        score3 = Math.round(semanticRatio * WEIGHTS.semantic);
      } catch (e) {
        if (process.env.DEBUG) console.error('semanticMatchFn error', e);
        score3 = 0;
      }
    }

    // Use HumanScanner for achievements / transferable / tech currency
    const humanRes = await this.human.simulateHumanScan(resumeText, jobDescription, atsResult);
    const instant = humanRes.breakdown.instantRecognition || 0;
    const credibility = humanRes.breakdown.credibilityIndicators || 0;

    // Quick heuristics: assume quantified achievements weight mostly in credibility
    const score4 = Math.round((credibility / 95) * WEIGHTS.quantified);
    // Transferable skills map from instant recognition (title, skills visible)
    const score5 = Math.round((instant / 95) * WEIGHTS.transferable);
    // tech currency – crude mapping using ats preferred skills match rate
    const techCurrencyRaw = atsResult.breakdown.keywordMatching || 0;
    const score6 = Math.round((techCurrencyRaw / 95) * WEIGHTS.techCurrency);

    const total = Math.min(
      100,
      score1 + score2 + score3 + score4 + score5 + score6 + score7
    );

    // reuse RealityScorer for band mapping
    const likelihood = this.reality.determineLikelihood(total);

    const result = {
      score: total,
      likelihood,
      breakdown: {
        spellingGrammar: score1,
        exactKeyword: score2,
        semanticMatch: score3,
        quantified: score4,
        transferable: score5,
        techCurrency: score6,
        layout: score7,
      },
      raw: { atsResult, humanRes, quality },
    };

    return result;
  }

  // Backwards-compatibility alias
  async checkHearingBackLikelihood(params) {
    return this.check(params);
  }

  // Cleanup method for React component unmounting
  dispose() {
    // No resources to cleanup in this implementation
    // but keeping the method for interface compatibility
  }
} 